<template>
  <div class="workflow" data-test="workflow-edit">
    <WdsPage>
      <template #nav>
        <WdsStack class="tw-w-full tw-fixed">
          <div class="sidebar-displacement nav-displacement"></div>
          <WdsStack justify-space-between class="tw-w-full tw-px-8">
            <WdsStack align-center gap-lg>
              <WdsButtonIcon data-test="edit-workflow-close-button" @click="$emit('close')">
                <WdsIcon name="chevron-left" />
              </WdsButtonIcon>
              <WdsText>{{ tree[0]?.vModel.name ?? $t('New workflow') }}</WdsText>
            </WdsStack>
            <WdsStack align-center gap-lg>
              <transition name="fade">
                <WdsText v-if="successfullySaved && !loading">{{ $t('Saved') }}</WdsText>
              </transition>
              <WdsSpinner v-if="loading" indeterminate />
              <WdsToggle v-model:checked="live" data-test="workflow-live">
                {{ $t('Live') }}
              </WdsToggle>
              <WdsButton
                data-test="update-workflow-button"
                :disabled="loading"
                primary
                @click="save"
              >
                {{ $t('Update') }}
              </WdsButton>
            </WdsStack>
          </WdsStack>
        </WdsStack>
      </template>
      <WdsStack>
        <div class="sidebar-displacement"></div>
        <div class="tw-pb-10 tree-wrapper">
          <EditorNodeRender :nodes="tree" @add="add" @remove="remove" />
        </div>
      </WdsStack>
    </WdsPage>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRefs, unref, watch } from 'vue'
import {
  WdsButton,
  WdsButtonIcon,
  WdsIcon,
  WdsPage,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsToggle,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import EditorNodeRender from '@/components/Workflows/Pages/Editor/EditorNodeRender.vue'
import Node from '@/components/Workflows/Pages/Editor/Nodes/Node'
import NodeTrigger from '@/components/Workflows/Pages/Editor/Nodes/NodeTrigger/NodeTrigger'
import { ApprovalStatus } from '@/gql/graphql'
import { NetworkError } from '@/utils/notification'
import { deepClone } from '@/utils/tools'
import useApolloError from '@/composables/useApolloError'
import { graphql, useFragment } from '@/gql'
import { WorkflowFragment } from '@/components/Workflows/Pages/Workflow/graphql/queries'
import { mapWorkflowToNodeTree } from '@/components/Workflows/utils'

window.scrollTo({ top: 0, behavior: 'smooth' }) // When we get loaded in we should scroll to the top of the page

const emit = defineEmits(['close', 'newId'])

const props = defineProps<{
  workflowId: string
}>()

const { workflowId: workflowIdUsed } = toRefs(props)

const { result, loading: workflowQueryLoading } = useQuery(
  graphql(/* GraphQL */ `
    query WorkflowQuery($id: ID!, $usersFirst: Int!, $usersPage: Int) {
      approval(id: $id) {
        ...WorkflowFragment
      }
    }
  `),
  () => ({ id: workflowIdUsed.value, usersFirst: 10, usersPage: 1 }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const {
  mutate,
  onError: updatingWorkflowOnError,
  onDone: updatingWorkflowOnDone,
  loading: updatingWorkflow,
} = useMutation(
  graphql(/* GraphQL */ `
    mutation UpdateWorkflow($input: UpdateWorkflowInput!) {
      updateWorkflow(input: $input) {
        id
      }
    }
  `),
  {
    clientId: 'publicApiCustomFields',
    refetchQueries: ['approvals', 'Workflows'], // Should update the lists ids to the new version
  },
)

// Tried a computed version which did not work for some reason.
// The underlying vue components stop rendering and it is no longer reactive.
const tree: Node[] = reactive([])

const successfullySaved = ref(false)
updatingWorkflowOnDone((response) => {
  const id = response.data?.updateWorkflow?.id
  successfullySaved.value = true

  // eslint-disable-next-line no-return-assign
  setTimeout(() => (successfullySaved.value = false), 1000)
  // Should update our id in the browser so on reload we should hit the proper version
  if (id) {
    emit('newId', id)

    if (tree[0].root) {
      tree[0].id = id
    }
  }
})

const loading = computed(
  () => (workflowQueryLoading.value || updatingWorkflow.value) && !successfullySaved.value,
)

const live = computed({
  get() {
    return tree?.[0]?.vModel.status === ApprovalStatus.Active
  },
  set(status) {
    if (tree.length > 0) {
      tree[0].vModel.status = status ? ApprovalStatus.Active : ApprovalStatus.Inactive
    }
  },
})

watch(
  result,
  () => {
    const workflow = useFragment(WorkflowFragment, result.value?.approval)

    if (workflow?.id !== workflow?.latestVersionId) {
      emit('newId', workflow?.latestVersionId)
      return
    }

    if (workflow && tree.length === 0) {
      // Alright this does not look elegant or ideal. But it works.
      // There is an undocumented issue with vue's reactivity system where if we add all the children first and then
      // assign them with reactivity then the children's functions don't reflect ie adding removing etc. But
      // if we first add the parent as reactive and then add the children then everything works as expected.

      const root = new NodeTrigger(workflow, {
        key: workflow.id,
      })
      tree.push(root)
      mapWorkflowToNodeTree(workflow, tree[0])
    } else if (tree.length === 1 && workflow) {
      tree[0].vModel = deepClone(unref(workflow)) // Update the root node's vmodel information
    }
  },
  { immediate: true },
)

updatingWorkflowOnError((error) => {
  const { validationErrors } = useApolloError(error)

  if (validationErrors.value) {
    const validationErrorKeys = Object.keys(validationErrors.value)

    validationErrorKeys.forEach((key) => {
      new NetworkError({
        msg: validationErrors.value[key],
      }).dispatch()
    })
  } else if (error.networkError) {
    new NetworkError({
      msg: error.networkError.message,
    }).dispatch()
  }
})

const save = async () => {
  try {
    const inputRaw = tree[0].compile()
    const root = inputRaw.root.pop()

    if (root !== undefined) {
      const input = {
        ...inputRaw,
        root, // Covers edge case
      }
      await mutate({ input })
    }
  } catch (ex: any) {
    new NetworkError({
      ex,
      msg: ex.message,
    }).dispatch()
  }
}

const remove = (node: Node) => {
  node.remove()
}

const add = (node: Node) => {
  if (node.add) {
    node.add()
  }
}
</script>

<style lang="scss" scoped>

.workflow {
  min-height: 100%;
  .tree-wrapper {
    max-width: 100%;
    @media (min-width: 1280px) {
      max-width: calc(100% - 268px);
    }
    :deep(> .workflow-row) {
      justify-content: normal;
    }

    // This bit finds the very first node and hides the :before content. This hides the potential "tail" that shows the
    // nodes relationship for the very first node, since it does not have a parent.
    :deep(> .workflow-row > .node-hierarchy > .parent-node) {
      .workflow-rule {
        &:before {
          content: none;
        }
      }
    }
  }
  :deep(.wds-page) {
    max-height: 100%;
    // Fixes an issue with multiple scroll-bars
    .wds-right {
      overflow-x: auto;
      overflow-y: visible;
    }
    .wds-right-slot {
      max-width: 100%;
      min-height: 100%;

      & > .wds-stack {
        min-height: 100%;
      }
    }
  }
}

.sidebar-displacement {
  display: none;
  &.nav-displacement {
    @media (max-width: 1280px) {
      display: block;
      width: 42px;
    }
  }

  @media (min-width: 1280px) {
    display: block;
    width: 268px;
    height: 100%;
  }
}
</style>
